import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { AgentCommissionService, AgentCommissionDto, AgentCommissionFilterDto } from '../../../services/agent-commission.service';
import { ExternalDataService, Agent } from '../../../services/externalDataService.service';
import { PaginationComponent } from '../../shared/pagination/pagination.component';

@Component({
  selector: 'app-agent-commissions-list',
  standalone: true,
  imports: [CommonModule, FormsModule, PaginationComponent],
  templateUrl: './agent-commissions-list.component.html',
  styleUrl: './agent-commissions-list.component.css'
})
export class AgentCommissionsListComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  // Data properties
  commissions: AgentCommissionDto[] = [];
  agents: Agent[] = [];
  
  // Selection properties
  selectedCommissions: Set<string> = new Set();
  showSettlementModal = false;
  settlementNotes = '';
  isBatchProcessing = false;

  // Loading and error states
  loading = false;
  errorMessage = '';
  successMessage = '';
  
  // Pagination properties
  currentPage = 1;
  pageSize = 10;
  totalCount = 0;
  totalPages: number = 1;
  
  // Filter properties
  filters = {
    fromDate: '',
    toDate: '',
    agentId: '',
    isSettled: false as boolean | null // Default to unsettled
  };
  
  // Make Math available in template
  Math = Math;

  constructor(
    private agentCommissionService: AgentCommissionService,
    private externalDataService: ExternalDataService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadAgents();
    this.loadCommissions();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAgents(): void {
    this.externalDataService.getAllAgents(true)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (agents) => {
          this.agents = agents;
        },
        error: (error) => {
          console.error('Error loading agents:', error);
        }
      });
  }

  loadCommissions(): void {
    this.loading = true;
    this.errorMessage = '';
    this.selectedCommissions.clear();
    
    const filterParams: AgentCommissionFilterDto = {
      pageIndex: this.currentPage - 1,
      pageSize: this.pageSize,
      agentId: this.filters.agentId || undefined,
      fromDate: this.filters.fromDate ? new Date(this.filters.fromDate).toISOString() : undefined,
      toDate: undefined,
      isSettled: this.filters.isSettled === null ? undefined : this.filters.isSettled
    };

    if (this.filters.toDate) {
      const dateTo = new Date(this.filters.toDate);
      dateTo.setHours(23, 59, 59, 999);
      filterParams.toDate = dateTo.toISOString();
    }
    
    this.agentCommissionService.getAgentCommissions(filterParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          this.commissions = data.items;
          this.totalCount = data.totalCount;
          this.calculateTotalPages();
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading agent commissions:', error);
          this.errorMessage = 'Failed to load agent commissions. Please try again.';
          this.loading = false;
        }
      });
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadCommissions();
  }

  onPageSizeChange(pageSize: number): void {
    this.pageSize = pageSize;
    this.currentPage = 1;
    this.loadCommissions();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadCommissions();
  }

  applyFilters(): void {
    this.currentPage = 1;
    this.loadCommissions();
  }

  clearFilters(): void {
    this.filters = {
      fromDate: '',
      toDate: '',
      agentId: '',
      isSettled: null
    };
    this.currentPage = 1;
    this.loadCommissions();
  }

  hasActiveFilters(): boolean {
    return !!(this.filters.fromDate || 
              this.filters.toDate || 
              this.filters.agentId || 
              this.filters.isSettled !== null);
  }

  // Selection Methods
  toggleSelection(id: string): void {
    if (this.selectedCommissions.has(id)) {
      this.selectedCommissions.delete(id);
    } else {
      const commission = this.commissions.find(c => c.scrapCollectionId === id);
      if (commission && !commission.isAgentVoucherCommissionSettled) {
        this.selectedCommissions.add(id);
      }
    }
  }

  isAllSelected(): boolean {
    const unsettledOnPage = this.commissions.filter(c => !c.isAgentVoucherCommissionSettled);
    return unsettledOnPage.length > 0 && unsettledOnPage.every(c => this.selectedCommissions.has(c.scrapCollectionId));
  }

  toggleAll(event: any): void {
    if (event.target.checked) {
      this.commissions.forEach(c => {
        if (!c.isAgentVoucherCommissionSettled) {
          this.selectedCommissions.add(c.scrapCollectionId);
        }
      });
    } else {
      this.selectedCommissions.clear();
    }
  }

  getSelectedTotal(): number {
    return this.commissions
      .filter(c => this.selectedCommissions.has(c.scrapCollectionId))
      .reduce((sum, c) => sum + c.agentVoucherCommission, 0);
  }

  // Settlement Batch Processing
  openSettlementModal(): void {
    if (this.selectedCommissions.size === 0) return;
    this.settlementNotes = '';
    this.showSettlementModal = true;
  }

  closeSettlementModal(): void {
    this.showSettlementModal = false;
  }

  processSettlement(): void {
    if (this.selectedCommissions.size === 0 || this.isBatchProcessing) return;

    this.isBatchProcessing = true;
    const batchDto = {
      scrapCollectionIds: Array.from(this.selectedCommissions),
      isSettled: true,
      notes: this.settlementNotes
    };

    this.agentCommissionService.updateAgentCommissionSettlementBatch(batchDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isBatchProcessing = false;
          this.showSettlementModal = false;
          this.successMessage = `Successfully settled ${this.selectedCommissions.size} commissions.`;
          this.selectedCommissions.clear();
          this.loadCommissions();
          
          // Clear success message after 5 seconds
          setTimeout(() => this.successMessage = '', 5000);
        },
        error: (error) => {
          console.error('Error processing settlement:', error);
          this.errorMessage = 'Failed to process settlement batch. Please try again.';
          this.isBatchProcessing = false;
        }
      });
  }

  private calculateTotalPages(): void {
    this.totalPages = Math.ceil(this.totalCount / this.pageSize) || 1;
  }

  formatDate(dateString: string | null): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  formatCurrency(amount: number): string {
    return `₹${amount.toFixed(2)}`;
  }
}
